import {ComplaintTaskRow} from '../../graphql/complaintTask/types';

/** 当前阶段的时限：未响应看响应时限，已响应看处理时限；为空 = 不考核。 */
export const stageDueAt = (row: ComplaintTaskRow): string | null | undefined =>
  row.actualStartAt ? row.resolveDueAt : row.responseDueAt;

const time = (iso?: string | null) => (iso ? Date.parse(iso) : Number.NaN);

/**
 * 紧急度（PRD 手机版）：没响应的按响应时限、已响应的按处理时限，时限越早越靠前；没时限的殿后；
 * 同时限按派单时间先后，最后按 taskId 保证顺序稳定。
 */
export const byUrgency = (a: ComplaintTaskRow, b: ComplaintTaskRow): number => {
  const da = time(stageDueAt(a));
  const db = time(stageDueAt(b));
  if (!Number.isNaN(da) || !Number.isNaN(db)) {
    if (Number.isNaN(da)) return 1;
    if (Number.isNaN(db)) return -1;
    if (da !== db) return da - db;
  }
  const aa = time(a.assignedAt);
  const ab = time(b.assignedAt);
  if (!Number.isNaN(aa) && !Number.isNaN(ab) && aa !== ab) return aa - ab;
  return a.taskId - b.taskId;
};

/**
 * 逐条处理的顺序：按紧急度排，「先跳过」的按跳过先后排到队尾。
 * 跳过只影响本次浏览顺序，刷新页面后重新按紧急度排（H5，10-14 确认）。
 */
export const focusOrder = (rows: ComplaintTaskRow[], skipped: number[]): ComplaintTaskRow[] => {
  const sorted = [...rows].sort(byUrgency);
  const skippedSet = new Set(skipped);
  const byId = new Map(sorted.map((r) => [r.taskId, r]));
  const tail = skipped.map((id) => byId.get(id)).filter((r): r is ComplaintTaskRow => !!r);
  return [...sorted.filter((r) => !skippedSet.has(r.taskId)), ...tail];
};

/**
 * 跳过一条后的跳过列表：一轮全部跳完（队列里每条都跳过了）就清空，从最紧急的重新来。
 */
export const skipTask = (rows: ComplaintTaskRow[], skipped: number[], taskId: number): number[] => {
  const next = [...skipped.filter((id) => id !== taskId), taskId];
  const nextSet = new Set(next);
  return rows.every((r) => nextSet.has(r.taskId)) ? [] : next;
};

export type CountdownTone = 'response' | 'resolve' | 'overdue' | 'none' | 'resolved';

export interface Countdown {
  tone: CountdownTone;
  /** 剩余（未超时）或已超（超时）毫秒；不考核 / 已解决时为空 */
  ms?: number;
}

/**
 * 大字倒计时：响应阶段黄、处理阶段绿、超时红（PRD 手机版）；时限为空显示「不考核」。
 */
export const countdownOf = (row: ComplaintTaskRow, nowMs: number): Countdown => {
  if (row.status === 70) return {tone: 'resolved'};
  const due = time(stageDueAt(row));
  if (Number.isNaN(due)) return {tone: 'none'};
  const left = due - nowMs;
  if (left < 0) return {tone: 'overdue', ms: -left};
  return {tone: row.actualStartAt ? 'resolve' : 'response', ms: left};
};
